import { ROOT_URL_HOST } from '@/shared/constants/contactConstants';

import { useUserStore } from '@/entities/user/store';
import { useLocationStore } from '@/entities/location/store/location';
import { buildLawyerMeta, buildLawyerBreadcrumbs } from '@/pages/lawyer/lib/lawyerMeta';
import { buildLawyerPageGraph } from '@/pages/lawyer/lib/schemaConstants';
import { buildAbsoluteUrl, type SchemaNode } from '@/shared/lib/schema';
import { unwrapData } from '@/shared/utils/unwrapApiResponse';
import { isCalendarVisibleForViewer } from '@/pages/lawyer/lib/calendarVisibility';
import { LAWYER_INFO } from '@/pages/lawyer/model/keys';
import { getErrorStatus } from '@/shared/utils/getErrorStatus';
import { scrollToSection, SECTION_SCROLL_OFFSET } from '@/shared/utils/scrollBehavior';

import type { ILawyerDetail } from '@/entities/lawyer/model/types/lawyer';
import type { Breadcrumb } from '@/shared/ui/AppBreadcrumbs/model/types/appBreadcrumbs';
import type {
  LawyerMeta,
  NavigationLink,
} from '@/pages/lawyer/model/types/useLawyerDetail';

const NAVIGATION_LINKS: NavigationLink[] = [
  { id: 1, to: '#main-profile', text: 'Основное' },
  { id: 2, to: '#experience', text: 'Опыт' },
  { id: 3, to: '#feedbacks', text: 'Отзывы' },
  { id: 4, to: '#contacts', text: 'Контакты' },
  { id: 5, to: '#services', text: 'Цены' },
];

const useLawyerDetail = () => {
  const route = useRoute();
  const instance = getCurrentInstance();
  const pagePath: string = route.path;
  const { $lawyerService } = useNuxtApp();
  const isMobile: boolean = useIsMobile();
  const userStore = useUserStore();
  const locationStore = useLocationStore();
  const { cities } = storeToRefs(locationStore);

  let observer: IntersectionObserver | null = null;
  let sectionsMutationObserver: MutationObserver | null = null;
  let isAlive = true;
  let isHashScrollPending = false;
  let hashScrollRun = 0;
  const observedSections = new WeakSet<Element>();

  const url: ComputedRef<string> = computed(() => `${ROOT_URL_HOST}${route.path}`);

  const pageAsyncData = useAsyncData(
    `lawyer:${route.path}`,
    async () => {
      const slug = String(route.params.slug);
      const lawyer = unwrapData(await $lawyerService.getLawyerBySlug(slug));

      if (!lawyer) {
        throw createError({ statusCode: 404, statusMessage: 'Юрист не найден' });
      }

      return {
        lawyer,
        meta: buildLawyerMeta(lawyer),
        breadcrumbs: buildLawyerBreadcrumbs(lawyer, cities.value),
      };
    },
    { watch: [() => route.path] },
  );

  const { data, status, error } = pageAsyncData;

  const lawyerInfo: ComputedRef<ILawyerDetail | null> = computed(
    () => data.value?.lawyer ?? null,
  );
  const meta: ComputedRef<LawyerMeta> = computed(
    () => data.value?.meta ?? { meta_title: '', meta_description: '' },
  );
  const breadcrumbs: ComputedRef<Breadcrumb[]> = computed(
    () => data.value?.breadcrumbs ?? [],
  );
  const isShowSkeleton: ComputedRef<boolean> = computed(() => status.value === 'pending');
  const isCompany: ComputedRef<boolean> = computed(() =>
    Boolean(lawyerInfo.value?.company),
  );

  const isShowCalendar: ComputedRef<boolean> = computed(() =>
    isCalendarVisibleForViewer(lawyerInfo.value, {
      isAuth: userStore.isAuth,
      isRoleTypeUser: userStore.isRoleTypeUser,
    }),
  );

  const schemaGraph: ComputedRef<SchemaNode[]> = computed(() =>
    lawyerInfo.value
      ? buildLawyerPageGraph(lawyerInfo.value, {
          pageUrl: buildAbsoluteUrl(route.path),
          breadcrumbs: breadcrumbs.value,
        })
      : [],
  );

  const getPageRoot = (): ParentNode => {
    const root: unknown = instance?.proxy?.$el;
    return root instanceof Element ? root : document;
  };

  const observeNewSections = (): void => {
    if (!observer) return;
    for (const section of getPageRoot().querySelectorAll('.lawyer-section')) {
      if (observedSections.has(section)) continue;
      observedSections.add(section);
      observer.observe(section);
    }
    if (isHashScrollPending) scrollToHash();
  };

  const observeSections = (): void => {
    if (typeof window === 'undefined' || !('IntersectionObserver' in window)) return;
    if (observer) {
      observeNewSections();
      return;
    }

    const nav = getPageRoot().querySelector('.navigation-links');
    const container = getPageRoot().querySelector('.lawyer-page__navigation-sections');
    if (!nav || !container) return;

    const handleIntersect = (entries: IntersectionObserverEntry[]): void => {
      for (const entry of entries) {
        const spy = nav.querySelector(`[data-scroll="#${entry.target.id}"]`);
        spy?.classList.remove('_active');
        if (entry.isIntersecting) spy?.classList.add('_active');
      }
    };

    observer = new IntersectionObserver(handleIntersect, {
      rootMargin: '-25% 0% -75% 0%',
    });
    observeNewSections();

    if ('MutationObserver' in window) {
      sectionsMutationObserver = new MutationObserver(observeNewSections);
      sectionsMutationObserver.observe(container, { childList: true, subtree: true });
    }
  };

  const scrollToHash = (): void => {
    hashScrollRun += 1;
    if (!route.hash || route.path !== pagePath) return;
    const el = getPageRoot().querySelector(`[id="${CSS.escape(route.hash.slice(1))}"]`);
    isHashScrollPending = !el;
    if (!el) return;
    const run = hashScrollRun;
    scrollToSection(el, SECTION_SCROLL_OFFSET, () => isAlive && run === hashScrollRun);
  };

  const onSkeletonChange = (hidden: boolean): void => {
    if (hidden) return;
    nextTick(() => {
      observeSections();
      scrollToHash();
    });
  };

  watch(isShowSkeleton, onSkeletonChange);
  watch(() => route.hash, scrollToHash);

  watchEffect(() => {
    if (error.value) {
      showError(createError({ statusCode: getErrorStatus(error.value), fatal: true }));
    }
  });

  onMounted(() => {
    if (isShowSkeleton.value) return;
    observeSections();
    scrollToHash();
  });

  onBeforeUnmount(() => {
    isAlive = false;
    observer?.disconnect();
    sectionsMutationObserver?.disconnect();
  });

  provide(LAWYER_INFO, lawyerInfo);
  provide('isShowSkeleton', isShowSkeleton);

  useSeoMeta({
    title: () => meta.value.meta_title || 'Юристы и адвокаты',
    description: () => meta.value.meta_description,
    keywords: () => meta.value.meta_keywords,
    ogTitle: () => meta.value.meta_title,
    ogDescription: () => meta.value.meta_description,
    ogSiteName: 'Сервис по подбору юристов, адвокатов - Юрик.ру',
    ogType: 'website',
    ogUrl: () => url.value,
    ogImage: () => lawyerInfo.value?.urlAvatar || `${ROOT_URL_HOST}/og-image.png`,
    ogImageWidth: () => (lawyerInfo.value?.urlAvatar ? undefined : 600),
    ogImageHeight: () => (lawyerInfo.value?.urlAvatar ? undefined : 600),
    ogLocale: 'ru',
  });

  useHead({
    meta: [
      {
        property: 'vk:image',
        content: () => lawyerInfo.value?.urlAvatar || `${ROOT_URL_HOST}/og-image.png`,
      },
    ],
  });

  return {
    pageReady: pageAsyncData,
    pageError: error,
    lawyerInfo,
    meta,
    breadcrumbs,
    navigationLinks: NAVIGATION_LINKS,
    isShowSkeleton,
    isCompany,
    isShowCalendar,
    isMobile,
    schemaGraph,
  };
};

export default useLawyerDetail;
